import { defineStore, storeToRefs } from 'pinia'
import { computed, ComputedRef, ref, shallowRef, triggerRef, unref, watch } from 'vue'
import { ColumnMeta, DataRow, Table } from '../types/components/data-rendering'
import { useTransformStore } from './helpers/useTransformStore'
import { setDefaultConditions, setGroupSort } from './helpers/useSetDefaultConditions'
import { StoresModel } from '../models/StoresModel'
import { useTaskProfiles } from './useTaskProfiles'
import { useProgressiveHash, parseBool } from '../utils'
import { MaybeRef } from '@vueuse/core'
import { useStore } from '.'

type UseStoreProfilesReturn = {
  getAllStoreData: ComputedRef<DataRow[]>
  /** Returns store profiles */
  getStores: ComputedRef<Table>
  /**Get stores hash */
  storesHash: () => Promise<string>
  /** Sets the stores ref to an array of store profiles */
  setStores: (data: DataRow[], options?: { writeStores: boolean }) => Promise<void>
  /** Adds a single store, currently only used for Recap Previews */
  addStore: (row: DataRow) => void
  /** Returns store profiles editable data*/
  getEditedStores: ComputedRef<DataRow[]>
  /** Resets edited flags to f */
  resetStoreEditedFlags: () => void
  /** Get store, key defaults to store_id */
  getStore: (value: number | string, key?: string) => DataRow | undefined
  /** Get single item */
  getStoreEditableData: (value: number | string, key?: string) => DataRow | undefined
  /** save array of items */
  setStoreEditableItem: (
    newData: { [key: string]: number | string },
    searchParams: { key?: string; value: number | string }
  ) => void
  /**Gets work view */
  getWorkView: ComputedRef<Table>
  getTaskData: (storeID: MaybeRef<string | number>, options?: { bulkRaiFilter?: boolean }) => Table
  getPickListData: (storeID: MaybeRef<string | number>) => Table
  resetStoreState: () => void
}

export const useStoreProfiles = defineStore('storeProfiles', (): UseStoreProfilesReturn => {
  const allStoreData = shallowRef<DataRow[]>([])
  const getAllStoreData = computed(() => allStoreData.value)
  const { writeStores } = StoresModel()
  const skip = ref(false)
  watch(
    allStoreData,
    async () => {
      //since all new data, write to db and clear existing stores from record
      if (!skip.value) {
        console.log('writing stores')
        await writeStores(allStoreData.value, true)
      } else {
        console.log('NOT writing stores')
        //we've skipped write, now reset state
        skip.value = false
      }
    },
    { deep: true }
  )
  const resetStoreState = () => {
    skip.value = true
    allStoreData.value = []
  }
  const getStores = computed((): Table => {
    const { taskData: tasks } = storeToRefs(useTaskProfiles())
    const storeTaskCountHashMap = tasks.value.reduce((acc: any, task: any) => {
      const key = String(task.store_id)
      if (key in acc) {
        const newData: { [key: string]: number } = acc as { [key: string]: number }
        newData[key] = newData[key] + 1
        return newData
      } else {
        const newData = acc
        newData[key] = 1
        return newData
      }
    }, {} as { [key: string]: number })
    const storeTableMeta: { [key: string]: any; columns: { [key: string]: ColumnMeta } } = {
      columns: {
        store_id: {
          label: {
            en_US: 'Store ID',
            fr_CA: 'ID du magasin',
            es_MX: 'ID de la tienda',
          },
          type: 'string',
        },
        store_name: {
          label: { en_US: 'Store Name', fr_CA: 'Nom du magasin', es_MX: 'Nombre de la tienda' },
          type: 'string',
        },
        store_number: {
          label: { en_US: 'Store Number', fr_CA: 'Numéro de magasin', es_MX: 'Número de tienda' },
          type: 'string',
        },
        chain_short_name: {
          label: { en_US: 'Chain', fr_CA: 'Chaîne', es_MX: 'Nombre de la cadena' },
          type: 'string',
        },
        street: {
          label: { en_US: 'Street', fr_CA: 'Rue', es_MX: 'Calle' },
          type: 'string',
        },
        city: {
          label: { en_US: 'City', fr_CA: 'Ville', es_MX: 'Ciudad' },
          type: 'string',
        },
        state: {
          label: { en_US: 'State', fr_CA: 'Province', es_MX: 'Estado' },
          type: 'string',
        },
        task_count: {
          label: {
            en_US: 'Task Count',
            fr_CA: 'Nombre de tâches',
            es_MX: '',
          },
          type: 'number',
        },
        market: {
          label: {
            en_US: 'Market',
            fr_CA: 'Marché',
            es_MX: 'Mercado',
          },
          type: 'string',
        },
        area: {
          label: {
            en_US: 'Area',
            fr_CA: 'Secteur',
            es_MX: 'Área',
          },
          type: 'string',
        },
        territory: {
          label: {
            en_US: 'Territory',
            fr_CA: 'Territoire',
            es_MX: 'Territorio',
          },
          type: 'string',
        },
        store_type: {
          label: {
            en_US: 'Network/Non-network',
            fr_CA: 'Réseau / Hors réseau',
            es_MX: 'Tipo de Tienda',
          },
          type: 'string',
        },
        store_status: {
          label: {
            en_US: 'Store status (open by default)',
            fr_CA: 'Statut du magasin (ouvert par défaut)',
            es_MX: 'Estado de la Tienda',
          },
          type: 'string',
        },
      },
    }
    const storeTableData: DataRow[] = []
    for (let index = 0; index < allStoreData.value.length; index++) {
      const store = allStoreData.value[index]
      const retData: DataRow = {}

      for (const key of Object.keys(storeTableMeta.columns)) {
        retData[key] = store[key]
        if (key == 'store_type') {
          retData[key] = parseBool(retData[key]) ? 'Network' : 'Non-Network'
        }
      }
      retData['task_count'] = storeTaskCountHashMap[String(store.store_id)]
        ? storeTaskCountHashMap[String(store.store_id)]
        : 0

      retData['_index'] = storeTableData.length
      storeTableData.push(retData)
    }

    return {
      meta: storeTableMeta,
      data: storeTableData,
    }
  })
  const storesHash = async () => {
    return await useProgressiveHash(
      allStoreData.value.map((store) => {
        store.edited = 'f'
        return store
      })
    )
  }
  const getEditedStores = computed(() => {
    return allStoreData.value.filter((store) => parseBool(store.edited))
  })
  const setStores = async (
    data: DataRow[],
    options: { writeStores?: boolean } = { writeStores: true }
  ) => {
    if (options.writeStores == false) {
      skip.value = true
    }
    allStoreData.value = data
  }
  const resetStoreEditedFlags = () => {
    allStoreData.value = allStoreData.value.map((store) => {
      store.edited = 'f'
      return store
    })
  }
  const addStore = (row: DataRow) => {
    allStoreData.value.push(row)
    triggerRef(allStoreData)
  }
  const getStore = (value: number | string, key = 'store_id') => {
    return allStoreData.value.find((item) => {
      return item[key] === value
    })
  }

  const getStoreEditableData = (value: number | string, key = 'store_id') => {
    const store = getStore(value, key)!
    const {
      no_of_coupon_zones,
      manned_checkout_number,
      self_checkout_number,
      cart_size_id,
      cart_count,
      pedestal_id,
      no_of_pedestals,
    } = store

    return {
      no_of_coupon_zones,
      manned_checkout_number,
      self_checkout_number,
      large_carts_1sd: cart_size_id == 1 ? cart_count : 0,
      large_carts_std: cart_size_id == 2 ? cart_count : 0,
      small_carts_1sd: cart_size_id == 3 ? cart_count : 0,
      small_carts_std: cart_size_id == 4 ? cart_count : 0,
      small_carts_thin: cart_size_id == 5 ? cart_count : 0,
      liberty_pedestals: pedestal_id == 1 ? no_of_pedestals : 0,
      evolve_pedestals: pedestal_id == 2 ? no_of_pedestals : 0,
      other_pedestals: pedestal_id == 3 ? no_of_pedestals : 0,
      e10_pedestals: pedestal_id == 4 ? no_of_pedestals : 0,
      p20_pedestals: pedestal_id == 5 ? no_of_pedestals : 0,
      noAdPanel_pedestals: pedestal_id == 6 ? no_of_pedestals : 0,
    }
  }
  const getStoreIndex = (value: number | string, key = 'store_id') => {
    return allStoreData.value.findIndex((store) => {
      return Number(store[key]) == Number(value)
    })
  }
  const setStoreEditableItem = (
    newData: { [key: string]: number | string },
    searchParams: { key?: string; value: number | string }
  ) => {
    const storeIndex = getStoreIndex(searchParams.value)
    if (storeIndex > -1) {
      for (const key of Object.keys(newData)) {
        allStoreData.value[storeIndex][key] = newData[key]
      }
      allStoreData.value[storeIndex].edited = 't'
      triggerRef(allStoreData)
    }
  }

  const getWorkView = computed(() => {
    const newData: Table = {
      meta: {
        columns: {
          store_id: {
            label: {
              en_US: 'Store ID',
              fr_CA: 'ID du magasin',
              es_MX: 'ID de la tienda',
            },
            type: 'string',
          },
          store_number: {
            label: { en_US: 'Store Number', fr_CA: 'Numéro de magasin', es_MX: 'Número de tienda' },
            type: 'string',
          },
          store_name: {
            label: { en_US: 'Store Name', fr_CA: 'Nom du magasin', es_MX: 'Nombre de la tienda' },
            type: 'string',
          },
          street: {
            label: { en_US: 'Street', fr_CA: 'Rue', es_MX: 'Calle' },
            type: 'string',
          },
          city: {
            label: { en_US: 'City', fr_CA: 'Ville', es_MX: 'Ciudad' },
            type: 'string',
          },
          state: {
            label: { en_US: 'State', fr_CA: 'Province', es_MX: 'Estado' },
            type: 'string',
          },
          chain_short_name: {
            label: { en_US: 'Chain', fr_CA: 'Chaîne', es_MX: 'Nombre de la cadena' },
            type: 'string',
          },

          territory: {
            label: {
              en_US: 'Territory',
              fr_CA: 'Territoire',
              es_MX: 'Territorio',
            },
            type: 'string',
          },
        },
      },
      data: [],
    }

    const taskInfoStoreHashMap = storeToRefs(useTaskProfiles()).taskData.value.reduce(
      (acc, task: DataRow) => {
        //find upper day
        const upperDay = new Date()
        upperDay.setDate(upperDay.getDate() + 7)

        const taskStartDate = new Date(task.task_start_date)
        const taskEndDate = new Date(task.task_end_date)

        if (taskStartDate <= upperDay) {
          //first init key in hash map, if not already done
          if (!(String(task.store_id) in acc)) {
            acc[String(task.store_id)] = {
              latestStartDate: taskStartDate,
              earliestEndDate: taskEndDate,
              earliestStartDate: taskStartDate,
              taskCount: 0,
            }
          }
          //keep new tasks start date if it is the latest
          if (taskStartDate.getTime() >= acc[String(task.store_id)].latestStartDate.getTime()) {
            acc[String(task.store_id)].latestStartDate = taskStartDate
          }
          //keep new tasks end date if it is earlier
          if (taskEndDate.getTime() <= acc[String(task.store_id)].earliestEndDate.getTime()) {
            acc[String(task.store_id)].earliestEndDate = taskEndDate
          }

          //keep new tasks start date if it is the earliest
          if (taskStartDate.getTime() <= acc[String(task.store_id)].earliestStartDate.getTime()) {
            acc[String(task.store_id)].earliestStartDate = taskStartDate
          }

          ++acc[String(task.store_id)].taskCount
        }
        return acc
      },
      {}
    )

    for (let index = 0; index < allStoreData.value.length; index++) {
      const store = allStoreData.value[index]
      const thisStoresTasks:
        | undefined
        | {
            taskCount: number
            latestStartDate: Date
            earliestEndDate: Date
            earliestStartDate: Date
          } = taskInfoStoreHashMap[String(store.store_id)]
      //skip if no store
      if (!thisStoresTasks) continue
      //skip if no tasks
      if (thisStoresTasks.taskCount == 0) continue
      //add latest start date and earliest end date
      const latestStartDate = thisStoresTasks.latestStartDate
      const earliestEndDate = thisStoresTasks.earliestEndDate
      const earliestStartDate = thisStoresTasks.earliestStartDate
      const taskCount = thisStoresTasks.taskCount
      const {
        store_id,
        store_name,
        store_number,
        street,
        city,
        state,
        chain_short_name,
        territory,
      } = store
      newData.data.push({
        _index: newData.data.length,
        store_id,
        store_name,
        store_number,
        // reformat dates to MM/DD/YYYY
        latestStartDate: latestStartDate
          ? `${
              latestStartDate.getMonth() + 1
            }/${latestStartDate.getDate()}/${latestStartDate.getFullYear()}`
          : '',
        earliestEndDate: earliestEndDate
          ? `${
              earliestEndDate.getMonth() + 1
            }/${earliestEndDate.getDate()}/${earliestEndDate.getFullYear()}`
          : '',
        earliestStartDate: earliestStartDate
          ? `${
              earliestStartDate.getMonth() + 1
            }/${earliestStartDate.getDate()}/${earliestStartDate.getFullYear()}`
          : '',
        taskCount: taskCount,
        street: street,
        city: city,
        state: state,
        chain_short_name: chain_short_name,
        territory: territory,
      })
    }

    const { colstore_id, colstore_name, colstore_number, ...cols } = newData.meta.columns
    newData.meta.columns = {
      store_id: colstore_id,
      store_name: colstore_name,
      store_number: colstore_number,
      earliestStartDate: {
        label: { en_US: 'Start Week', es_MX: '', fr_CA: 'Première semaine' },
        type: 'date',
      },
      taskCount: {
        label: { en_US: 'Task Count', es_MX: '', fr_CA: 'Nombre de tâches' },
        type: 'string',
      },
      ...cols,
      latestStartDate: {
        label: { en_US: 'Optimal Start', es_MX: '', fr_CA: 'Départ optimal' },
        type: 'date',
      },
      earliestEndDate: {
        label: { en_US: 'Optimal End', es_MX: '', fr_CA: 'Fin optimale' },
        type: 'date',
      },
    }

    const returnTable = setGroupSort(newData, {
      // groupBy: 'placement_section',
      sortBy: 'earliestStartDate',
    })

    return returnTable
  })

  /**util to filter tasks by store_id */
  const getTaskDataForStore = (storeID: MaybeRef<string | number>) => {
    const { taskTable } = storeToRefs(useTaskProfiles())
    const data = { ...taskTable.value }

    data.data = data.data.filter((row) => {
      return row.store_id == unref(storeID)
    })

    return data
  }

  //store profile's task index
  const getTaskData = (storeID: MaybeRef<string | number>) => {
    const data = { ...getTaskDataForStore(unref(storeID)) }
    data.data = data.data
      .filter((task: DataRow) => {
        const upperDay = new Date()
        upperDay.setDate(upperDay.getDate() + 7)
        return new Date(task.task_start_date) <= upperDay
      })
      //RE NPUB-292: sort by task type order
      .sort((a, b) => {
        return ('' + a['task_order']).localeCompare('' + b['task_order'], undefined, {
          numeric: true,
        })
      })
    const transformed = useTransformStore(data).subset(
      [
        'attachment',
        'task_status',
        'task_status_id',
        'artwork_id',
        'task_description', //??artwork description?
        'product_line',
        'task_type',
        'job_name',
        'task_start_date',
        'task_end_date',
        'test_indicator',
        'work_type',
        'kit',
        'task_id',
        'order_type',
        'bulk_updated', //keep, hidden
        'placement_section',
      ],
      {
        renameKeys: [
          {
            from: 'task_description',
            to: {
              label: {
                en_US: 'Task Description (Artwork description)',
                fr_CA: 'Description de la tâche (description du visuel)',
                es_MX: '',
              },
              type: 'string',
            },
          },
          {
            from: 'task_status',
            to: {
              label: {
                en_US: 'Status',
                fr_CA: 'Statut',
                es_MX: '',
              },
              type: 'string',
              options: [
                { en_US: 'Open', fr_CA: 'Ouvert', es_MX: '' },
                { en_US: 'Completed', fr_CA: 'Fermé', es_MX: '' },
                { en_US: 'Bulk', fr_CA: 'En bloc', es_MX: '' },
              ],
            },
          },
          {
            from: 'kit',
            to: {
              label: {
                en_US: 'Kit info',
                fr_CA: 'Info Kit',
                es_MX: '',
              },
              type: 'string',
            },
          },
          {
            from: 'attachment',
            to: {
              label: {
                en_US: 'Attachments',
                fr_CA: 'Pièces jointes',
                es_MX: '',
              },
              type: 'string',
            },
          },
          {
            from: 'order_type',
            to: {
              label: {
                en_US: 'Order Type',
                fr_CA: 'Order Type',
                es_MX: '',
              },
              type: 'string',
              hide: true,
            },
          },
          {
            from: 'task_status_id',
            to: {
              label: {
                en_US: '',
                fr_CA: '',
                es_MX: '',
              },
              type: 'string',
              hide: true,
            },
          },
        ],
      }
    )

    transformed.data.forEach((task, index) => {
      //@ts-expect-error bool work
      task.test_indicator = parseBool(task.test_indicator)

      task.task_status = task.bulk_updated
        ? { en_US: 'Bulk', fr_CA: 'En bloc', es_MX: '' }[locale.value]
        : task.task_status == 'Open'
        ? { en_US: 'Open', fr_CA: 'Ouvert', es_MX: '' }[locale.value]
        : { en_US: 'Completed', fr_CA: 'Fermé', es_MX: '' }[locale.value]

      task._index = index
    })

    const conditions = setDefaultConditions(transformed, [
      {
        col: 'task_status',
        operation: 'isAnyOf',
        value: [{ en_US: 'Open', fr_CA: 'Ouvert', es_MX: '' }[locale.value]],
      },
    ])

    const returnTable = setGroupSort(conditions, {
      groupBy: 'placement_section',
      sortBy: 'task_end_date',
    })

    return returnTable
  }
  const store = storeToRefs(useStore())
  const locale = computed(() => store.user.value.language)

  //store picklist
  const getPickListData = (storeID: MaybeRef<string | number>) => {
    const data = getTaskDataForStore(unref(storeID))
    data.data = data.data.filter((task: DataRow) => {
      const upperDay = new Date()
      upperDay.setDate(upperDay.getDate() + 7)
      return new Date(task.task_start_date) <= upperDay
    })
    const transformedNoDef = useTransformStore(data).subset(
      [
        'artwork_id',
        'artwork',
        'product_line',
        'task_type',
        'work_type',
        'job_name',
        'orderline_id',
        'placement_section',
        'kit',
        'task_start_date',
        'task_end_date',
        'ad_quantity',
        'ad_type',
        'task_id',
        'order_type',
      ],
      {
        renameKeys: [
          {
            from: 'work_type',
            to: {
              label: {
                en_US: 'Work Type',
                fr_CA: 'Type de travail',
                es_MX: '',
              },
              type: 'string',
            },
          },
        ],
      }
    )
    transformedNoDef.data = transformedNoDef.data.map((row, index) => {
      return {
        ...row,
        _index: index,
      }
    })
    const transformed = setDefaultConditions(transformedNoDef, [
      {
        col: 'task_type',
        operation: 'isAnyOf',
        value: [
          { en_US: 'Install', fr_CA: 'Installation', es_MX: '' }[locale.value],
          { en_US: 'Other', fr_CA: 'Autre', es_MX: '' }[locale.value],
        ],
      },
    ])
    const returnTable = setGroupSort(transformed, {
      groupBy: 'product_line',
      sortBy: 'task_end_date',
    })

    return returnTable
  }

  return {
    resetStoreState,
    getStores,
    storesHash,
    setStores,
    addStore,
    getEditedStores,
    resetStoreEditedFlags,
    getStoreEditableData,
    getStore,
    setStoreEditableItem,
    getWorkView,
    getTaskData,
    getPickListData,
    getAllStoreData,
  }
})
